import { useCallback, useEffect, useState } from 'react';
import {
  User as FirebaseUser,
  applyActionCode as firebaseApplyActionCode,
  sendEmailVerification as firebaseSendEmailVerification,
} from 'firebase/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import { isEqual } from 'lodash';
import posthog from 'posthog-js';

import { auth } from '../lib/firebase-init';
import { User } from './types';
import { AuthContext } from './auth-context';

const sendEmailVerification = async () => {
  if (auth.currentUser !== null) {
    await firebaseSendEmailVerification(auth.currentUser);
  }
};

const getToken = (firebaseUser: FirebaseUser | null) => async () => {
  if (firebaseUser) {
    const decodedUser = await firebaseUser.getIdTokenResult();
    const forceRefresh = decodedUser.claims.email_verified !== firebaseUser.emailVerified;
    return await firebaseUser.getIdToken(forceRefresh);
  }
  return 'missing-token';
};

interface AuthContextProviderProps {
  children: React.ReactNode;
}

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const [firebaseUser, firebaseLoading] = useAuthState(auth);
  const [user, setUser] = useState<User | null>(null);

  const applyActionCode = useCallback(async (code: string) => {
    await firebaseApplyActionCode(auth, code);
    if (auth.currentUser === null) {
      return null;
    }
    await auth.currentUser.reload();

    const user = {
      email: auth.currentUser.email ?? '',
      emailVerified: auth.currentUser.emailVerified,
    };

    setUser(user);

    return user;
  }, []);

  useEffect(() => {
    const listener = auth.onIdTokenChanged(async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser !== null) {
        setUser((prevUser) => {
          posthog.identify(firebaseUser.uid, {
            email: firebaseUser.email,
          });
          const newUser = {
            email: firebaseUser.email ?? '',
            emailVerified: firebaseUser.emailVerified,
          };
          if (isEqual(prevUser, newUser)) {
            return prevUser;
          }
          return newUser;
        });
      } else {
        setUser(null);
      }
    });

    return () => {
      listener();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading: firebaseLoading,
        getToken: getToken(firebaseUser ?? null),
        logOut: () => auth.signOut(),
        sendEmailVerification,
        applyActionCode,
      }}>
      {children}
    </AuthContext.Provider>
  );
};
